'use client'

import { FC, useCallback } from 'react'
import {
    useCreateCancellationMutation,
    useParticipantLoginMutation,
} from '../../../../lib/graphql/generated/hooks'
import {
    InputFragment,
    InputValueFragment,
} from '../../../../lib/graphql/generated/types'
import {
    useNotification,
    useRouter,
    useTranslation,
    useViewerUser,
} from '../../../../lib/hooks'
import { useToken } from '../../../../lib/providers/tokenProvider/tokenProvider'
import decodeTokenExpiration from '../../../../lib/utils/decodeTokenExpiration'
import { Button, ButtonTheme } from '../../../base'
import { InputFieldDialog, InputFieldDialogValues } from '../../../common'
import valuesToInputFieldValues from '../../registration/details/utils/valuesToInputFieldValues'

interface Props {
    eventId: string
    inputFieldValues?: InputValueFragment[]
    inputFields: InputFragment[]
}

const DeregisterButton: FC<Props> = props => {
    const { t } = useTranslation()
    const notification = useNotification()
    const router = useRouter()
    const viewerUser = useViewerUser()
    const [, createCancellation] = useCreateCancellationMutation()
    const [, participantLogin] = useParticipantLoginMutation()
    const { token, setToken } = useToken()

    const optOut = useCallback(
        async (values: InputFieldDialogValues) => {
            try {
                const { data, error } = await createCancellation({
                    token,
                    eventId: props.eventId,
                    inputFieldValues: valuesToInputFieldValues(
                        values,
                        props.inputFields
                    ),
                })

                if (error) {
                    throw error
                }

                if (data?.viewer?.event?.createCancellation?.inputError) {
                    notification.alert(
                        data.viewer.event.createCancellation.inputError.message
                    )
                } else {
                    notification.confirm(t('event:deregister.confirmation'))
                }

                const accessIdentifier =
                    data?.viewer?.event?.createCancellation?.object?.participant
                        .accessIdentifier

                // Skip token creation when already authenticated
                if (accessIdentifier && !viewerUser) {
                    const participantLoginResult = await participantLogin({
                        accessIdentifier,
                    })

                    if (participantLoginResult.error) {
                        throw participantLoginResult.error
                    }

                    const _token =
                        participantLoginResult.data?.authentication
                            ?.createTokenByParticipant?.token
                    const expires = decodeTokenExpiration(_token)

                    if (!_token || !expires) {
                        throw Error(t('common:error.internal'))
                    }

                    setToken(_token, true, expires)
                }

                router.refresh()
            } catch (e) {
                console.error(e)
                notification.alert(t('common:error.internal'))
            }
        },
        [
            createCancellation,
            notification,
            participantLogin,
            props.eventId,
            props.inputFields,
            router,
            setToken,
            t,
            token,
            viewerUser,
        ]
    )

    return (
        <>
            <InputFieldDialog
                buttonText={t('event:deregister.title')}
                inputFieldValues={props.inputFieldValues}
                inputFields={props.inputFields}
                onSubmit={optOut}
                promptText={t('event:deregister.prompt')}
                title={t('event:deregister.title')}
            >
                {open => (
                    <Button onClick={open} theme={ButtonTheme.Secondary}>
                        {t('event:deregister.title')}
                    </Button>
                )}
            </InputFieldDialog>
        </>
    )
}

export default DeregisterButton
